import React, {useState, useEffect} from 'react';
import styled from 'styled-components';
import html2canvas from 'html2canvas'; // Import html2canvas

import {Row} from '@bootcamp/web/src/components/Grid';
import ari from '@bootcamp/shared/src/assets/educators/ari.gif';
import datLogo from '@bootcamp/shared/src/assets/icons/logo/dat-logo.png';
import {useBootcampConfigContext} from '@bootcamp/web/src/contexts/BootcampConfig';
import {useUserDataContext} from '@bootcamp/web/src/contexts/UserData';
import {Chiclet, RoundButton} from '@bootcamp/web/src/components/UI';
import {Color} from '@styled-icons/fluentui-system-filled/Color';
import {ArrowDownload} from '@styled-icons/fluentui-system-filled/ArrowDownload';
import {LoadingSpinner, Toggle, StyledBlurb, StyledHeader, StyledLabel, StyledTable} from './shared';
import {PerformanceChart} from './PerformanceChart';
import {getResults} from '@bootcamp/shared/src/util/scoring';
import {TextTooltip} from '@bootcamp/web/src/bootcamps/pages/CreateTest/components/shared';
import {Tooltip} from 'react-tooltip'
import {TableOverflowOuterWrapper, TableOverflowInnerWrapper} from '@bootcamp/web/src/bootcamps/components/Performance/shared';
import {createPathFromConfigMatch, findInConfigById} from '@bootcamp/shared/src/util';

import theme from '@bootcamp/shared/src/styles/theme';
import debounce from 'lodash.debounce';
import { Body3 } from '../../../components/Typography/next';
import { format } from 'date-fns';

const StyledTextTooltip = styled(TextTooltip)`
  margin-left: 8px;
  svg {
    height: 14px;
    width: 14px;
  }
  color: ${({theme}) => theme.overlays.opacity.light._400};
`;

const Header = styled.div`
  display: flex;
  margin-bottom: 16px;
  align-items: center;
`;

const Container = styled.div`
  border-radius: 8px;
  margin-bottom: 32px;
`;

const HeatmapToggle = styled(Chiclet)`
  ${({theme}) => theme.mediaQueries.tablet} {
    margin-left: 0;
  }
`;
const TestResultTable = styled(StyledTable)`
  min-width: 800px;
  tr:first-child {
    td:first-child {
      width: 148px;
    }
  }
  td:last-child {
    text-align: left;
  }
`;

const ShareButton = styled(RoundButton)`
  margin-left: auto;
  padding: 8px 12px;
  font-size:
`;
const Wrapper = styled.div`
  margin-bottom: 16px;
  padding: 0px 32px;
  ${({theme}) => theme.mediaQueries.tablet} {
    padding: 0 16px;
  }
`;
const ToggleRow = styled(Row)`
  ${({theme}) => theme.mediaQueries.tablet} {
    flex-direction: column;
    gap: 8px;
  }
`;
const DownloadIcon = styled(ArrowDownload)`
  color: #000000;
  width: 16px;
  height: 16px;
`;
const CellContentContainer = styled.span`
`;
const StyledTooltip = styled(Tooltip)`
  min-width: 250px;
  border-radius: 8px;
  padding: 8px 12px;
  background: ${({theme}) => theme.colors.special.tuxedo};
  opacity: 1;
`;
const TooltipContentContainer = styled.div`
`;
const TooltipDate = styled(Body3)`
  margin-bottom: 4px;
`;
const TooltipTestTitle = styled(Body3)`
  margin-bottom: 4px;
  font-weight: 700;
`;
const TooltipScoreArea = styled.div`
  display: flex;
  flex-direction: column;
`;
const TooltipScore = styled.div`
  display: flex;
  justify-content: space-between;
  color: ${({theme}) => theme.colors.typographyPalette.tertiary};
  gap: 8px;

  &:first-child {
    color: white;
  }
`;

const TooltipScoreTop = styled(Body3)`
  font-weight: 700;
`;

const TooltipScoreBottom = styled(Body3)`
  font-weight: 400;
`;

function formatDate(date) {
  try {
    return format(new Date(date), 'MMM d, yyyy');
  } catch (error) {
    return ''
  }
}
const TooltipContent = ({
  date,
  testTitle,
  score,
  avgScore,
}) => {

  const formattedDate = formatDate(date);
  const title = testTitle?.replace('Test', '');

  return (
    <TooltipContentContainer>
      <TooltipDate>{formattedDate}</TooltipDate>
      <TooltipTestTitle>{title}</TooltipTestTitle>
      <TooltipScoreArea>
        <TooltipScore>
          <TooltipScoreTop>Your Score</TooltipScoreTop>
          <TooltipScoreTop>{score}</TooltipScoreTop>
        </TooltipScore>
        <TooltipScore>
          <TooltipScoreBottom>Bootcamp Avg.</TooltipScoreBottom>
          <TooltipScoreBottom>{avgScore}</TooltipScoreBottom>
        </TooltipScore>
      </TooltipScoreArea>
    </TooltipContentContainer>
  );
}

const CellContent = ({score, onMouseEnter, disabled}) => {
  if (disabled) return score;

  return (
    <CellContentContainer data-tooltip-id={'test-score-tooltip'} onMouseEnter={onMouseEnter}>
      {score}
    </CellContentContainer>
  );
};

const Test = ({fetching, dataByTest=[], averages, testAverages, sortOrder}) => {
  const [tooltipProps, setTooltipProps] = useState({});

  const {isBootcampPlusStudent} = useUserDataContext();
  const {config} = useBootcampConfigContext();

  const rows = dataByTest
    .map((subjects, index) => ({
      cells: [
          { content: `Test #${index+1}` },
        ...subjects.map(({score, testId, testProgressId, date, testNumber, subject, ...rest}) => {
          const configMatch = findInConfigById(config, testId);
          const fltConfigHit = [config?.studyTools?.[0]?.content?.find(({ id }) => id === testId)];
          const configRoute = configMatch
            ? createPathFromConfigMatch(configMatch)
            : fltConfigHit
            ?`/full-length/test-${index + 1}`
            : '';

          const testRoute = `/dat${configRoute}`;
          const cellDisabled = score === '-';
          // const avgScorePercentage = testAverages?.[testId]?.avgScore * 100;
          const testTitle = `${subject} Test #${testNumber}`;
          const avgScore = 390 // getResults(avgScorePercentage, testTitle, subject);
          const tooltipDisplayTitle = fltConfigHit[0]
            ? fltConfigHit[0].name
            : testTitle

          const getCellThemeProps = (score) => {
            if (cellDisabled) return {};

            const themes = [
              theme.colors.interfacePalette.red,
              theme.colors.interfacePalette.yellow,
              theme.colors.interfacePalette.green,
            ];

            let themeIndex;

            themeIndex = score <= 370
              ? 0
              : score <= 410
              ? 1
              : 2;

            return {
              background: themes[themeIndex].light,
              color: themes[themeIndex].dark
            }
          };

          return {
            to: !testId ? null : `${testRoute}/review?id=${testId}&testProgressId=${encodeURIComponent(testProgressId)}&testRoute=${encodeURIComponent(testRoute)}`,
            disabled: cellDisabled,
            content: <CellContent score={score} disabled={cellDisabled || !testId} onMouseEnter={() => setTooltipProps({score, avgScore, date, testTitle: tooltipDisplayTitle})}/>,
            ...getCellThemeProps(score)
            }
        }),
        ],
    }))

  const allSubjectsPresent = averages.map(({content}) => content).every(score => score !== '-');
  const average = allSubjectsPresent ? averages[6].content : 'N/A';
  const scoringAverageMax = Math.min(average + 20, 600);

  // filter out PAT
  const baseFeedback = average === 'N/A'
    ? <span>Check back once you’ve completed a full practice test for an estimated DAT score. Be sure you complete all {isBootcampPlusStudent ? '15' : '10'} practice tests before your DAT to get the most accurate estimated score.</span>
    : <span>Based on your performance, I'd <strong>estimate an AA score of {average} - {scoringAverageMax}</strong> on the DAT. Don't focus too much on the estimate - instead, continue to review your areas of knowledge. Make sure you complete all the practice tests to learn from your mistakes and get a more accurate estimated score. Note your First Attempt is your most accurate score prediction.<br/><br/>
      Starting March 1, 2025, the DAT switched to a 3-digit scoring scale (200-600). <a style={{color: 'white'}} href="https://bootcamp.com/blog/upcoming-changes-to-dat-scoring-and-reporting-march-1-2025" target="_blank">Learn more here.</a>
    </span>

  const feedback = sortOrder === 'ASC'
    ? baseFeedback
    : 'Your Latest Attempts are not the most accurate score predictions. Focus on the First Attempts instead!'

  // Function to add the container, header, and extra text before downloading the image
  const downloadTableAsImage = debounce(() => {
    // Create a container with padding
    const container = document.createElement('div');
    container.style.padding = '16px';
    container.style.background = theme.colors.brandPalette.royal.default;
    container.style.borderRadius = '8px';
    container.style.width = '1024px';

    // Create a header for "Score Report"
    const header = document.createElement('div');
    header.style.display = 'flex';
    header.style.justifyContent = 'space-between';
    header.style.marginBottom = '16px';

    const leftText = document.createElement('h2');
    leftText.innerText = 'Score Report';
    leftText.style.margin = '0';
    leftText.style.color = 'white';
    leftText.style.fontFamily =  "proxima-nova";
    leftText.style.fontSize =  "24px";
    leftText.style.fontWeight =  "700";
    leftText.style.lineHeight =  "30px";

    const logo = document.createElement('img');
    logo.src = datLogo;
    // logo.style.width = '100px';
    logo.style.height = '30px';
    logo.style.margin = '0';

    // Append the left and right text to the header
    header.appendChild(leftText);
    header.appendChild(logo);

    // Clone the table element to keep the original intact
    const tableElement = document.getElementById('score-table').cloneNode(true);

    // Append header and table to the container
    container.appendChild(header);
    container.appendChild(tableElement);

    // Append container to body temporarily for rendering
    document.body.appendChild(container);

    // Convert the container to an image
    html2canvas(container).then((canvas) => {
      const link = document.createElement('a');
      link.href = canvas.toDataURL('image/png');
      link.download = 'score_report.png';
      link.click();

      // Clean up by removing the container after the image is downloaded
      document.body.removeChild(container);
    });
  }, 450, {leading: true, trailing: false});

  return (
    <Container>
      <StyledTooltip id={'test-score-tooltip'} content={() => <TooltipContent {...tooltipProps}/>} place={'right'}/>
      <Wrapper>
        <Header>
          <StyledLabel style={{marginBottom: 0}}>Score Report</StyledLabel>
          {/* <StyledTextTooltip id="order-tooltip">Use First Attempt for the most accurate score prediction.</StyledTextTooltip> */}
          <ShareButton
            onClick={downloadTableAsImage}
            mini
            dark
            active
            iconPosition={'left'}
            IconComponent={<DownloadIcon/>}>
              Download
          </ShareButton>
        </Header>
        <StyledBlurb
          icon={ari}
          text={feedback}
          lightMode
        />
      </Wrapper>
      <TableOverflowOuterWrapper>
        <TableOverflowInnerWrapper id="score-table">
          <TestResultTable
            hideChevrons
              rows={[
              { cells: [
                { content: '' },
                { content: 'BIO' },
                { content: 'GC' },
                { content: 'OC' },
                { content: 'PAT' },
                { content: 'RC' },
                { content: 'QR' },
                { content: 'AA' },
              ]
              },
              {cells: [
                { content: 'Average' },
                ...averages,
              ]},
              ...rows
            ]}
          />
        </TableOverflowInnerWrapper>
      </TableOverflowOuterWrapper>
    </Container>
  );
};

Test.defaultProps = {};
Test.propTypes = {};

export default Test;